import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { agreementsApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { usePrincipal } from '@/auth/PrincipalContext';
import { AmendmentCreateForm } from '@/forms/AmendmentCreateForm';
import { ErrorBanner, Skeleton } from '@/components/primitives';
import { ApiError } from '@/types/errors';
import { canAmendAgreement } from '@/auth/permissions';
import { NotFoundPage } from './NotFoundPage';

export function AmendmentCreatePage() {
  const { id } = useParams<{ id: string }>();
  const principal = usePrincipal();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);

  const q = useQuery({
    queryKey: qk.agreement(id ?? ''),
    queryFn: () => agreementsApi.get(id!),
    enabled: !!id,
  });

  if (q.error instanceof ApiError && q.error.status === 404) {
    return <NotFoundPage />;
  }
  if (q.isError) {
    return (
      <ErrorBanner
        message="Couldn't load the source agreement."
        onRetry={() => void q.refetch()}
      />
    );
  }
  if (q.isPending || !q.data) {
    return <Skeleton rows={4} height="20px" />;
  }

  const amend = canAmendAgreement(principal, q.data);
  if (amend.rendering !== 'visible') {
    return (
      <div>
        <h1 ref={h1Ref} tabIndex={-1}>
          Amend agreement
        </h1>
        <ErrorBanner
          message={
            amend.rendering === 'disabled'
              ? amend.tooltip ?? "You can only amend agreements you own."
              : "You don't have permission to amend agreements."
          }
        />
      </div>
    );
  }

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          Amend agreement
        </h1>
      </header>
      <AmendmentCreateForm source={q.data} principal={principal} />
    </div>
  );
}
